import {
	usePublishChat,
	useUnpublishChat,
} from "@src/components/agent-chat/mutations/chat-mutations";
import {
	WhiteboardDocumentId,
	parseChatDocumentId,
} from "@src/components/collaboration/document-id";
import { ChatPermission, PlanFamily } from "@src/gql/graphql";
import { handleError } from "@src/utils/error-handling";
import { showSuccessNotification } from "@src/utils/notifications";
import { useCallback, useMemo, useState } from "react";
import { useMyPlanSuspense } from "../../plan/queries/fetch-plan";
import { getChatShareLink } from "../chat-share-utils";
import type { PermissionType } from "../permission-combobox";
import type { ShareablePublication } from "../shareable-entity";
import { useCopyTooltip } from "./use-copy-tooltip";

export type ShareChatPanelStateArgs = {
	chatId: string;
	publication?: ShareablePublication | null;
};

export function useShareChatPanelState({
	chatId,
	publication,
}: ShareChatPanelStateArgs) {
	const documentId = useMemo(
		() => new WhiteboardDocumentId(parseChatDocumentId(chatId).toIdString()),
		[chatId],
	);
	const { mutateAsync: publishChat, isPending: isPublishingChat } =
		usePublishChat();
	const { mutateAsync: unpublishChat, isPending: isUnpublishingChat } =
		useUnpublishChat();
	const { data: plan } = useMyPlanSuspense();
	const triggerCopyTooltip = useCopyTooltip();

	const [isLinkCopiedTooltipOpen, setIsLinkCopiedTooltipOpen] = useState(false);
	const [activeTab, setActiveTab] = useState<string | null>("share");

	const publishedKeySignature = `${chatId}:${publication?.key ?? ""}`;
	const [localPublishedOverride, setLocalPublishedOverride] = useState<{
		signature: string;
		key: string | null;
	} | null>(null);

	const currentPublishedKey =
		localPublishedOverride?.signature === publishedKeySignature
			? localPublishedOverride.key
			: publication?.key;

	const isPublished = useMemo(
		() => currentPublishedKey != null,
		[currentPublishedKey],
	);

	const isStarterPlan = plan.planFamily === PlanFamily.Starter;
	const defaultPermission: PermissionType =
		isStarterPlan || (publication?.isReadonly ?? false)
			? "can view"
			: "can edit";
	const permissionSignature = `${chatId}:${isStarterPlan}:${publication?.isReadonly ?? false}`;
	const [localPermissionOverride, setLocalPermissionOverride] = useState<{
		signature: string;
		value: PermissionType;
	} | null>(null);

	const permission: PermissionType =
		localPermissionOverride?.signature === permissionSignature
			? localPermissionOverride.value
			: defaultPermission;

	const setPermission = useCallback(
		(value: PermissionType) => {
			setLocalPermissionOverride({
				signature: permissionSignature,
				value,
			});
		},
		[permissionSignature],
	);

	const handleCopyPublishLinkTooltip = useCallback(() => {
		triggerCopyTooltip(setIsLinkCopiedTooltipOpen);
	}, [triggerCopyTooltip]);

	const handleUpdatePublicPermission = useCallback(
		async (nextPermission: PermissionType) => {
			try {
				await publishChat({
					chatId,
					defaultPermission:
						nextPermission === "can edit"
							? ChatPermission.Write
							: ChatPermission.Read,
				});
				showSuccessNotification(
					"Success",
					nextPermission === "can edit"
						? "Chat allowed to be edited by anonymous users successfully"
						: "Chat denied to be edited by anonymous users successfully",
				);
			} catch (error) {
				handleError(error as Error, "Failed to update chat guest permission");
				setPermission(nextPermission === "can edit" ? "can view" : "can edit");
			}
		},
		[chatId, publishChat, setPermission],
	);

	const handleSwitchChange = useCallback(
		async (checked: boolean) => {
			try {
				if (checked) {
					const res = await publishChat({
						chatId,
						defaultPermission:
							permission === "can edit"
								? ChatPermission.Write
								: ChatPermission.Read,
					});
					if (res?.state?.shareKey) {
						setLocalPublishedOverride({
							signature: publishedKeySignature,
							key: res.state.shareKey,
						});
					}
					showSuccessNotification("Success", "Chat published successfully");
				} else {
					await unpublishChat(chatId);
					setLocalPublishedOverride({
						signature: publishedKeySignature,
						key: null,
					});
					showSuccessNotification("Success", "Chat unpublished successfully");
				}
			} catch (error) {
				handleError(error as Error, "Failed to update chat publication");
			}
		},
		[chatId, permission, publishChat, publishedKeySignature, unpublishChat],
	);

	const handlePermissionChange = useCallback(
		(value: PermissionType) => {
			if (isPublished && value !== permission) {
				void handleUpdatePublicPermission(value);
			}
			setPermission(value);
		},
		[handleUpdatePublicPermission, isPublished, permission, setPermission],
	);

	const clipboardPublishedLink = useMemo(() => {
		if (!currentPublishedKey) return null;
		return getChatShareLink(chatId, currentPublishedKey);
	}, [chatId, currentPublishedKey]);

	return {
		documentId,
		activeTab,
		setActiveTab,
		isLinkCopiedTooltipOpen,
		isPublished,
		permission,
		isLoading: isPublishingChat || isUnpublishingChat,
		handleCopyPublishLinkTooltip,
		handleSwitchChange,
		handlePermissionChange,
		clipboardPublishedLink,
	};
}
